<script setup>
import SectionTitle from '@weathernews/btos-vuecomponent/with-onebox-common-elements/src/components/SectionTitle.vue'

const props = defineProps({
  pageInfo: {
    type: Object,
    default: () => ({})
  }
})
const route = useRoute()
const tabList = [
  { text: '現在の映像', key: 'current' },
  { text: '平常時の様子', key: 'ordinary' }
]
const viewMaxIndex = ref(10)
const { data: cameraList } = await useAsyncData(
  `river-camera-list-${route.params.rvrsys}`,
  () => {
    if (route.params.rvrsys === undefined) return []
    return $fetch('/api/river/rvrsys/cameralist', {
      query: {
        path: route.params.rvrsys
      }
    })
  },
  { watch: [() => route.params.rvrsys] }
)
const sortedCameraList = ref([])

watchEffect(() => {
  if (cameraList.value && Array.isArray(cameraList.value)) {
    sortedCameraList.value = cameraList.value.slice(0, viewMaxIndex.value)
  }
})

const onClickTab = (index, key) => {
  sortedCameraList.value[index].isSelected = key
}

const onClickButton = async () => {
  const nextMax = Math.min(viewMaxIndex.value + 10, cameraList.value.length)
  const addList = cameraList.value.slice(viewMaxIndex.value, nextMax)
  await Promise.all(
    addList.map(async (item) => {
      const data = await $fetch(`https://aplapi.weathernews.jp/lba/river/api_observatory_detail${item.query}`)
      item.current = data.camera_list?.at(-1)?.url || ''
      item.ordinary = data.normal_img_url || ''
    })
  )
  sortedCameraList.value.push(...addList)
  viewMaxIndex.value = nextMax
}
</script>

<template>
  <div
    v-if="cameraList"
    class="riverList"
  >
    <div class="head">
      <SectionTitle :title="`${props.pageInfo.name}のライブカメラ一覧`" />
    </div>
    <template v-if="sortedCameraList.length > 0">
      <div
        v-for="(item, index) in sortedCameraList"
        :key="item.id"
        class="content"
      >
        <div
          v-if="index !== 0 && index % 5 === 0"
          :id="`ads-atf-ex-${index}`"
          class="ads-atf2"
        />
        <NuxtLink
          :to="`/${route.params.rvrsys}/?pid=${item.id.replace(/^cctv_/, '')}`"
          class="text"
        >
          {{ item.name }}
        </NuxtLink>
        <div class="block sp-size">
          <div class="image">
            <img
              v-if="item[item.isSelected]"
              :src="item[item.isSelected]"
              :loading="index < 4 ? 'eager' : 'lazy'"
              :fetchpriority="index < 4 ? 'high' : 'auto'"
            >
            <div
              v-else
              class="no-image"
            >
              <p class="no-image-text">
                カメラ画像が取得できませんでした
              </p>
            </div>
          </div>
          <div class="tabs">
            <p
              v-for="tab in tabList"
              :key="tab.key"
              :data-selected="item.isSelected === tab.key"
              class="tab"
              @click="onClickTab(index, tab.key)"
            >
              {{ tab.text }}
            </p>
          </div>
        </div>
        <div class="block pc-size">
          <div
            v-for="tab in tabList"
            :key="tab.key"
            class="image"
          >
            <img
              v-if="item[tab.key]"
              :src="item[tab.key]"
              :loading="index < 4 ? 'eager' : 'lazy'"
              :fetchpriority="index < 4 ? 'high' : 'auto'"
            >
            <div
              v-else
              class="no-image"
            >
              <p class="no-image-text">
                カメラ画像が取得できませんでした
              </p>
            </div>
            <p class="tab">
              {{ tab.text }}
            </p>
          </div>
        </div>
      </div>
    </template>
    <div
      v-if="cameraList.length !== sortedCameraList.length"
      class="bottom"
    >
      <CommonMoreButton
        text="もっと見る"
        direction="bottom"
        col="white"
        @click="onClickButton()"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.riverList {
  display: flex;
  flex-direction: column;
  gap: 10px;
  .head {
    display: flex;
    justify-content: space-between;
  }
  .tabs {
    display: flex;
    padding: 4px;
    border-radius: 100vmax;
    border: 1px solid #FFF;
    background-color: #EEF1F8;
    box-shadow: 0 1px 2px 0 rgb(53 105 192 / .4);
    .tab {
      padding: 4px 10px;
      color: #303030;
      font-size: 12px;
      font-weight: 600;
      line-height: 1em;
      text-align: center;
      &[data-selected="true"] {
        border-radius: 100vmax;
        background-color: #3569C0;
        box-shadow: 0 1px 2px 0 rgb(53 105 192 / .4);
        color: #FFF;
      }
    }
  }
  .content {
    display: flex;
    flex-direction: column;
    gap: 10px;
    .text {
      padding-left: 8px;
      border-left: 2px solid #0C419A;
      color: #3569C0;
      font-size: 14px;
      font-weight: 600;
      line-height: 18px;
      text-decoration: underline;
    }
    .image {
      aspect-ratio: 16 / 9;
      background-color: #303030;
      width: 100%;
      max-height: calc((100vw - 20px) *9 / 16);
      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
      .no-image {
        width: 100%;
        height: 100%;
        background-color: #f6f6f6;
        .no-image-text {
          width: 100%;
          height: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          color: #ff3f00;
          font-size: 12px;
          font-weight: 300;
          line-height: 1em;
          padding: 5px;
        }
      }
    }
    .block {
      display: flex;
      align-items: flex-end;
      flex-direction: column;
      gap: 5px;
    }
  }
  .bottom {
    display: flex;
    justify-content: center;
    button {
      padding: 10px 20px;
      background-color: #3569C0;
      color: #FFF;
      font-size: 14px;
      font-weight: 600;
      line-height: 1em;
      border-radius: 4px;
      border: none;
      cursor: pointer;
      &:hover {
        background-color: #2a4f8b;
      }
    }
  }
  @media (any-hover: hover) {
    .text {
      transition: opacity .2s ease-in-out;
      cursor: pointer;
      &:hover {
        opacity: .6;
      }
    }
  }
}
@media (max-width: 829px) {
  .riverList {
    .content {
      .pc-size {
        display: none;
      }
    }
  }
}
@media (min-width: 830px) {
  .riverList {
    .content {
      .text {
        font-size: 16px;
        line-height: 20px;
      }
      .image {
        position: relative;
      }
      .block {
        display: flex;
        flex-direction: row;
        gap: 10px;
      }
      .sp-size {
        display: none;
      }
      .tab {
        position: absolute;
        top: 6px;
        right: 6px;
        border-radius: 4px;
        background-color: #eef1f8cc;
        box-shadow: 0 1px 2px 0 rgb(53 105 192 / .4);
        padding: 10px 15px;
        color: #303030;
        font-size: 14px;
        font-weight: 600;
        line-height: 1em;
        letter-spacing: 0.25px;
      }
    }
  }
}
.ads-atf2 {
  text-align: center;
  margin: 0px auto;
  position: relative;
}
</style>
